/**
 * Image Loading Utility
 * Centralized system for optimizing image loading across the application
 */

// Default configuration
const DEFAULT_CONFIG = {
    mobile: {
        height: 200,
        quality: 85,
        maxPixelRatio: 4
    },
    desktop: {
        height: 450, // to be used for high res images URL
        quality: 85,
        maxPixelRatio: 2
    },
    breakpoint: 768
};


export const getOptimizedImageUrl = (image, options = {}) => {
    const config = { ...DEFAULT_CONFIG, ...options };
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth < config.breakpoint;

    // Get base URL from different image object formats
    let baseUrl = '';

    if (typeof image === 'string') {
        baseUrl = image;
    } else if (image?.fields?.file?.url) {
        // Contentful format
        baseUrl = image.fields.file.url;
    } else if (image?.url) {
        // Direct URL format
        baseUrl = image.url;
    } else {
        console.warn('Invalid image object provided to getOptimizedImageUrl:', image);
        return '';
    }

    // Ensure URL has proper protocol
    if (baseUrl.startsWith('//')) {
        baseUrl = `https:${baseUrl}`;
    }

    // Get dimensions from image object if available
    const originalWidth = image?.fields?.file?.details?.image?.width;
    const originalHeight = image?.fields?.file?.details?.image?.height;

    // Calculate target dimensions
    const targetConfig = isMobile ? config.mobile : config.desktop;
    let targetWidth = null;
    let targetHeight = null;
    let pixelRatio = Math.min(window.devicePixelRatio || 1, targetConfig.maxPixelRatio);

    // Check if we're constraining by width or height
    if (targetConfig.width) {
        // Constrain by width
        targetWidth = targetConfig.width * pixelRatio;
        if (originalWidth && originalHeight) {
            targetHeight = Math.round((targetWidth / originalWidth) * originalHeight);
        }
    } else if (targetConfig.height) {
        // Constrain by height
        targetHeight = targetConfig.height * pixelRatio;
        if (originalWidth && originalHeight) {
            targetWidth = Math.round((targetHeight / originalHeight) * originalWidth);
        }
    }

    // Build query parameters
    const queryParams = new URLSearchParams();

    if (targetWidth) {
        queryParams.append('w', targetWidth);
    }
    if (targetHeight) {
        queryParams.append('h', targetHeight);
    }
    queryParams.append('q', targetConfig.quality);
    queryParams.append('fm', 'jpg');

    return `${baseUrl}?${queryParams.toString()}`;
};


// THUMBNAIL URL max width or height
// Desktop > 250px x 2 = 500px
// Mobile > 200px

export const getThumbnailUrl = (image, options = {}) => {
    // Get original dimensions to determine orientation
    const originalWidth = image?.fields?.file?.details?.image?.width;
    const originalHeight = image?.fields?.file?.details?.image?.height;

    let thumbnailConfig;

    if (originalWidth && originalHeight) {
        const isPortrait = originalHeight > originalWidth;
        const isLandscape = originalWidth > originalHeight;

        if (isPortrait) {
            // For portraits: constrain by height
            thumbnailConfig = {
                mobile: {
                    height: 200,
                    quality: 85,
                    maxPixelRatio: 4
                },
                desktop: {
                    height: 500,
                    quality: 85,
                    maxPixelRatio: 2
                }
            };
        } else if (isLandscape) {
            // For landscapes: constrain by width
            thumbnailConfig = {
                mobile: {
                    width: 200,
                    quality: 85,
                    maxPixelRatio: 4
                },
                desktop: {
                    width: 500,
                    quality: 85,
                    maxPixelRatio: 2
                }
            };
        } else {
            // For square images: use height constraint
            thumbnailConfig = {
                mobile: {
                    height: 200,
                    quality: 85,
                    maxPixelRatio: 4
                },
                desktop: {
                    height: 500,
                    quality: 85,
                    maxPixelRatio: 2
                }
            };
        }
    } else {
        // Fallback to original config
        thumbnailConfig = {
            mobile: {
                height: 200,
                quality: 85,
                maxPixelRatio: 4
            },
            desktop: {
                height: 500,
                quality: 85,
                maxPixelRatio: 2
            }
        };
    }

    return getOptimizedImageUrl(image, { ...thumbnailConfig, ...options });
};

export const getThumbnailUrlHomePage = (image, options = {}) => {
    // Get original dimensions to determine orientation
    const originalWidth = image?.fields?.file?.details?.image?.width;
    const originalHeight = image?.fields?.file?.details?.image?.height;

    let thumbnailConfig;

    if (originalWidth && originalHeight) {
        const isPortrait = originalHeight > originalWidth;
        const isLandscape = originalWidth > originalHeight;

        if (isPortrait) {
            // For portraits: constrain by height
            thumbnailConfig = {
                mobile: {
                    height: 400,
                    quality: 85,
                    maxPixelRatio: 4
                },
                desktop: {
                    height: 850,
                    quality: 85,
                    maxPixelRatio: 1
                }
            };
        } else if (isLandscape) {
            // For landscapes: constrain by width
            thumbnailConfig = {
                mobile: {
                    width: 400,
                    quality: 85,
                    maxPixelRatio: 4
                },
                desktop: {
                    width: 850,
                    quality: 85,
                    maxPixelRatio: 1
                }
            };
        }
    } else {
        // Fallback to original config
        thumbnailConfig = {
            mobile: {
                height: 200,
                quality: 85,
                maxPixelRatio: 4
            },
            desktop: {
                height: 500,
                quality: 85,
                maxPixelRatio: 1
            }
        };
    }

    return getOptimizedImageUrl(image, { ...thumbnailConfig, ...options });
};



export const getThumbnailDimensions = (image) => {
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth < 768;

    // Get original dimensions
    const originalWidth = image?.fields?.file?.details?.image?.width;
    const originalHeight = image?.fields?.file?.details?.image?.height;

    if (originalWidth && originalHeight) {
        // Determine if image is portrait or landscape
        const isPortrait = originalHeight > originalWidth;
        const isLandscape = originalWidth > originalHeight;

        // Set maximum dimension based on orientation
        const maxDimension = 250;
        const pixelRatio = Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2);

        let finalWidth, finalHeight;

        if (isPortrait) {
            // For portraits: constrain by height
            finalHeight = maxDimension * pixelRatio;
            finalWidth = Math.round((finalHeight / originalHeight) * originalWidth);
        } else if (isLandscape) {
            // For landscapes: constrain by width
            finalWidth = maxDimension * pixelRatio;
            finalHeight = Math.round((finalWidth / originalWidth) * originalHeight);
        } else {
            // For square images: use height constraint
            finalHeight = maxDimension * pixelRatio;
            finalWidth = finalHeight;
        }

        return {
            width: finalWidth,
            height: finalHeight
        };
    }

    // Fallback dimensions
    return {
        width: isMobile ? 200 : 200,
        height: isMobile ? 200 : 200
    };
};


// FULL SCREEN URL max width or height
// Desktop > 700px x 2 = 1400px
// Mobile > 400px

export const getFullScreenUrl = (image, options = {}) => {
    const fullScreenConfig = {
        mobile: {
            height: 750,
            quality: 100,
            maxPixelRatio: 3
        },
        desktop: {
            height: 1400, // was 650
            quality: 85,
            maxPixelRatio: 1
        },
        breakpoint: 768
    };

    return getOptimizedImageUrl(image, { ...fullScreenConfig, ...options });
};


// FETCH FULL SCREEN PICTURES URL
// Desktop > original image (no resizing)
// Mobile > height 1000px max (width auto)

export const fetchFullScreenPictures = (image, options = {}) => {
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth <= 768;

    if (isMobile) {
        // On mobile, optimize to 1000px height
        const fetchFullScreenConfig = {
            mobile: {
                height: 1000,
                quality: 100,
                maxPixelRatio: 1
            },
            breakpoint: 768
        };
        return getOptimizedImageUrl(image, { ...fetchFullScreenConfig, ...options });
    } else {
        // On desktop, return original image URL without optimization
        let baseUrl = '';

        if (typeof image === 'string') {
            baseUrl = image;
        } else if (image?.fields?.file?.url) {
            baseUrl = image.fields.file.url;
        } else if (image?.url) {
            baseUrl = image.url;
        }

        // Ensure URL has proper protocol
        if (baseUrl.startsWith('//')) {
            baseUrl = `https:${baseUrl}`;
        }

        return baseUrl;
    }
};


// SLIDER PICTURES max width or height
// Desktop > 400 px = 800px
// Mobile > 300 px


export const getSliderUrl = (image, options = {}) => {
    const sliderConfig = {
        mobile: {
            height: 300,
            quality: 85,
            maxPixelRatio: 4
        },
        desktop: {
            height: 800,
            quality: 85,
            maxPixelRatio: 1
        }
    };

    return getOptimizedImageUrl(image, { ...sliderConfig, ...options });
};


export const preloadImage = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });
};


export const preloadImages = (urls) => {
    return Promise.all(urls.map(url => preloadImage(url)));
};


export const getImageDimensions = (image) => {
    if (image?.fields?.file?.details?.image) {
        return {
            width: image.fields.file.details.image.width,
            height: image.fields.file.details.image.height
        };
    }

    // Fallback to default dimensions
    return {
        width: '100%',
        height: 'auto'
    };
};


export const getFullScreenDimensions = (image) => {
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth < 768;

    // Get original dimensions
    const originalWidth = image?.fields?.file?.details?.image?.width;
    const originalHeight = image?.fields?.file?.details?.image?.height;

    if (originalWidth && originalHeight) {
        if (isMobile) {
            // Mobile: height constrained to 400px, calculate width
            const targetHeight = 400;
            const targetWidth = Math.round((targetHeight / originalHeight) * originalWidth);
            return {
                width: targetWidth,
                height: targetHeight
            };
        } else {
            // Desktop: height constrained to 1400px, calculate width
            const targetHeight = 1400;
            const targetWidth = Math.round((targetHeight / originalHeight) * originalWidth);
            return {
                width: targetWidth,
                height: targetHeight
            };
        }
    }

    // Fallback dimensions
    return {
        width: isMobile ? 'auto' : 'auto',
        height: isMobile ? 400 : 1400
    };
};


export const getFetchFullScreenDimensions = (image) => {
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth < 768;

    // Get original dimensions
    const originalWidth = image?.fields?.file?.details?.image?.width;
    const originalHeight = image?.fields?.file?.details?.image?.height;

    if (originalWidth && originalHeight) {
        if (isMobile) {
            // Mobile: height constrained to 1000px, calculate width
            const targetHeight = 1000;
            const targetWidth = Math.round((targetHeight / originalHeight) * originalWidth);
            return {
                width: targetWidth,
                height: targetHeight
            };
        } else {
            // Desktop: width constrained to 2000px, calculate height
            const targetWidth = 2000;
            const targetHeight = Math.round((targetWidth / originalWidth) * originalHeight);
            return {
                width: targetWidth,
                height: targetHeight
            };
        }
    }

    // Fallback dimensions
    return {
        width: isMobile ? 'auto' : 2000,
        height: isMobile ? 1000 : 'auto'
    };
};



export const isVideo = (image) => {
    let url = '';

    if (typeof image === 'string') {
        url = image;
    } else if (image?.fields?.file?.url) {
        url = image.fields.file.url;
    } else if (image?.url) {
        url = image.url;
    }

    return url.toLowerCase().endsWith('.mp4') ||
        url.toLowerCase().endsWith('.webm') ||
        url.toLowerCase().endsWith('.mov');
};


export const getVideoUrl = (video) => {
    let url = '';

    if (typeof video === 'string') {
        url = video;
    } else if (video?.fields?.file?.url) {
        url = video.fields.file.url;
    } else if (video?.url) {
        url = video.url;
    }

    if (url.startsWith('//')) {
        url = `https:${url}`;
    }

    return url;
}; 